import React, { useEffect, useRef, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { toast } from 'react-toastify';
import { ExportToCsv } from 'export-to-csv-fix-source-map';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import { FailToastContainer } from 'components/Toast/ToastContainer';
import { toRem } from 'design';
import { Align } from 'design/types';
import { useOnClickOutside } from 'hooks';
import { PayrollTipsExport } from 'modals';
import { ApiQueryOperator, IEmployeePayrollTip, IIntegration, IntegrationCategory, IntegrationType, ISite, ISiteIntegration } from 'types';
import { forceRender, sanitizeFileName, toTitleCase } from 'utils';
import { CSVFormatter, IPayrollIntegrationExportError, PayrollExportError, PayrollExportValidationType } from 'utils/csvformatter';
import * as API from '../../api';
import { ActionMenu, ActionMenuItem, ActionMenuList } from '../ActionMenu';
import { ModalFactory } from '../Modal';
import Button from './Button';

const BASE_CSV_OPTIONS = {
  fieldSeparator: ',',
  quoteStrings: '"',
  decimalSeparator: '.',
  showLabels: true,
  showTitle: false,
  useTextFile: false,
  useBom: true,
  useKeysAsHeaders: false,
};

interface IExportPayrollTipsCSVButtonProps {
  label?: string;
  disabled?: boolean;
  site?: ISite;
  query?: IApiQueryOptions;
}

const Layout = styled.div`
  display: flex;
  justify-content: flex-end;

  button.open svg {
    transform: rotate(180deg);
  }
`;

const StyledActionMenu = styled(ActionMenu)`
  top: ${toRem(45)};
  left: unset;
  margin-left: ${toRem(-146)};
  border-radius: ${toRem(8)};
`;

const getIntegrationName = (integration: ISiteIntegration) => {
  switch (integration.type) {
    case IntegrationType.ADP_RUN_EXPORT:
      return 'ADP Run';
    case IntegrationType.PAYLOCITY_EXPORT:
      return 'Paylocity';
    case IntegrationType.RESTAURANT365_EXPORT:
      return 'Restaurant365';
    default:
      return toTitleCase(integration.type.split('_', 1).join());
  }
};

const errorToast = () => toast(<FailToastContainer title="Error" message="Unable to download csv, please try again." />);

const ButtonExportPayrollTipsCSVAsync = ({ label = 'Export', disabled, site, query }: IExportPayrollTipsCSVButtonProps) => {
  const actionMenuRef = useRef(null);
  const [clicked, setClicked] = useState<boolean>(false);
  const [exportError, setExportError] = useState<IPayrollIntegrationExportError>(null);
  const [exportData, setExportData] = useState<[string, IEmployeePayrollTip[], IIntegration]>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);

  // Filter integrations to get payroll integrations
  const payrollIntegrations = site?.integrations?.filter((integration) => integration.categories?.includes(IntegrationCategory.PAYROLL));

  const startDate = (query.q || []).find((q) => q.key === 'payment_date' && q.operator === ApiQueryOperator.gte)?.value;
  const endDate = (query.q || []).find((q) => q.key === 'payment_date' && q.operator === ApiQueryOperator.lte)?.value;

  // Close action menu when click outside
  useOnClickOutside(actionMenuRef, () => setActionMenuOpen(false));

  const [showModal, hideModal] = useModal(() => {
    if (!exportError) return null;
    const handleModalClose = () => {
      setExportError(null);
      setExportData(null);
      hideModal();
    };
    const handleConfirm = async () => {
      try {
        setClicked(true);
        // force loading to display since exportCsv is not async
        await forceRender();
        exportCsv(...exportData);
      } catch (err: any) {
        errorToast();
      } finally {
        setClicked(false);
        handleModalClose();
      }
    };
    const hasMissingIds = exportError.type === PayrollExportValidationType.MISSING_EMPLOYEE_ID;
    return (
      <ModalFactory customModal onClose={handleModalClose} isActive>
        <PayrollTipsExport error={exportError} onClose={handleModalClose} onContinue={hasMissingIds ? handleConfirm : undefined} isProcessing={clicked} />
      </ModalFactory>
    );
  }, [exportError, exportData, clicked]);

  useEffect(() => {
    if (exportError) showModal();
  }, [exportError]);

  const exportCsv = (fileName: string, payrollTips: IEmployeePayrollTip[], integration?: IIntegration) => {
    // Format the CSV data
    const response = CSVFormatter.employeePayrollTips(site.id, startDate.toString(), endDate.toString(), payrollTips, integration);
    // Sanitize file name and export CSV
    const sanitizedFileName = sanitizeFileName(fileName);
    const csvExporter = new ExportToCsv({ ...BASE_CSV_OPTIONS, headers: response.headers, filename: sanitizedFileName });
    csvExporter.generateCsv(response.data || [[]]);
  };

  const handleExport = async (fileName: string, payrollIntegration?: ISiteIntegration) => {
    try {
      setClicked(true);
      setActionMenuOpen(false);

      // Fetch site integration details and employee payroll tips for date range
      const [integrationDetails, payrollTipsResponse] = await Promise.all([
        payrollIntegration ? API.Integrations.getIntegration(site.id, payrollIntegration.id) : undefined,
        API.Sites.getEmployeePayrollTips(site.id, startDate.toString(), endDate.toString(), { ...query, q: query.q?.filter((q) => q.key !== 'payment_date'), limit: 0, offset: 0 }),
      ]);

      if (integrationDetails) {
        const validationWarning = CSVFormatter.validateExportPayroll(site.id, payrollTipsResponse.body.data, integrationDetails.body);
        if (validationWarning?.type === PayrollExportValidationType.MISSING_EMPLOYEE_ID) {
          setExportData([fileName, payrollTipsResponse.body.data, integrationDetails.body]);
          throw new PayrollExportError(validationWarning);
        }
      }

      exportCsv(fileName, payrollTipsResponse.body.data, integrationDetails?.body);
    } catch (err: any) {
      if (err instanceof PayrollExportError) {
        setExportError(err.error);
      } else {
        errorToast();
      }
    } finally {
      setClicked(false);
    }
  };

  // Payroll integrations
  if (payrollIntegrations?.length > 0) {
    return (
      <Layout ref={actionMenuRef}>
        <Button
          data-cy="export-integrations-btn"
          appearance="primary"
          size="small"
          label="Export"
          className={actionMenuOpen ? 'open' : ''}
          icon="Dropdown"
          iconPosition={Align.RIGHT}
          isProcessing={clicked}
          disabled={disabled}
          onClick={() => setActionMenuOpen(!actionMenuOpen)}
        />
        <StyledActionMenu data-cy="payroll-export-menu" isVisible={actionMenuOpen} hideMobileHeader>
          <ActionMenuList>
            <ActionMenuItem key="payroll-export-csv" data-cy="payroll-export-menu-action" onClick={() => handleExport(`PayPeriod_${site.name}_${startDate}_${endDate}`)}>
              Export CSV
            </ActionMenuItem>
            {payrollIntegrations?.map((integration) => {
              const integrationName = getIntegrationName(integration);
              return (
                <ActionMenuItem key={`${integration.type}-${integration.id}`} data-cy="payroll-export-menu-action" onClick={() => handleExport(`${integrationName}_${site.name}_${startDate}_${endDate}`, integration)}>
                  {`Export for ${integrationName}`}
                </ActionMenuItem>
              );
            })}
          </ActionMenuList>
        </StyledActionMenu>
      </Layout>
    );
  }

  return <Button appearance="primary" size="small" label={label} isProcessing={clicked} disabled={disabled} onClick={() => handleExport(`PayPeriod_${site.name}_${startDate}_${endDate}`)} data-cy="export-btn" />;
};

export default ButtonExportPayrollTipsCSVAsync;
